import React, { useState } from 'react'
import { isNil } from 'ramda'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import {
  Button,
  MenuList,
  withStyles,
  Typography,
  makeStyles,
} from '@material-ui/core'
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined'

import { DownloadIcon } from 'app/components/icons'

import Menus from 'app/componentV4/Menu/Menus'
import Avatars from 'app/componentV4/Avatar/Avatars'
import Circular from 'app/componentV4/Loader/Circular'

import useEvent from './useEvent'

const useStyles = makeStyles({
  menus: {
    '& .MuiPaper-root': {
      width: 'max-content',
      maxHeight: '220px',
      overflowX: 'scroll',
      borderRadius: '12px',
      '& .MuiMenu-list': {
        padding: '0',
      },
    },
  },
})

const CustomsButton = withStyles((theme) => ({
  root: {
    padding: 0,
    minWidth: 'unset',
    width: 'fit-content',
    marginRight: '0.5rem',
    textTransform: 'lowercase',
    backgroundColor: 'transparent',
    color: theme.palette.sky[700],
    fontSize: '12px',
    fontWeight: '400',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&[disabled]': {
      color: theme.palette.grey[300],
    },
  },
}))(Button)

const EventInvitees = (props) => {
  const { id, data } = props
  const { going, maybe, notGoing } = data || {}

  const {
    state: { isGoing, isMaybe, isNotGoing, isLoading, subscriberList },
    handleDownload,
    handleToggleGoing,
    handleToggleMaybe,
    handleToggleNotGoing,
  } = useEvent(id)

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [anchorEl, setAnchorEl] = useState(null)

  const handleAnchorEl = (value) => {
    setAnchorEl(value)
  }

  const handleClickGoing = (event) => {
    handleAnchorEl(event?.currentTarget)
    handleToggleGoing(!isGoing)
  }

  const handleClickMaybe = (event) => {
    handleAnchorEl(event?.currentTarget)
    handleToggleMaybe(!isMaybe)
  }

  const handleClickNotGoing = (event) => {
    handleAnchorEl(event?.currentTarget)
    handleToggleNotGoing(!isNotGoing)
  }

  const renderSubscriberList = (isload, subscribers) => {
    return (
      <div>
        {isload ? (
          <div className="flex justify-center my-10">
            <Circular color="secondary" size={12} />
          </div>
        ) : (
          <div>
            {subscribers.map((subscriber) => {
              return (
                <MenuList key={subscriber.id}>
                  <div className="flex items-center space-x-3 px-5">
                    <Avatars
                      id={subscriber.id}
                      name={subscriber.displayName}
                      src={subscriber.profilePicture}
                      characters={subscriber.avatarCharacters}
                      className="w-24 h-24 text-12"
                    />
                    <Typography className="text-primary text-12">{`${subscriber.firstName} ${subscriber.lastName}`}</Typography>
                  </div>
                </MenuList>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const handleGoing = () => {
    return (
      <div>
        <CustomsButton
          size="small"
          aria-haspopup="true"
          aria-controls="going-list"
          disabled={going <= 0}
          onClick={handleClickGoing}
        >
          {formatMessage(
            {
              id: 'notificationTimeline.card.event.action.going',
            },
            { count: going },
          )}
          <span>&#44;</span>
        </CustomsButton>
        <Menus
          id="going-list"
          anchorEl={anchorEl}
          open={isGoing}
          onClose={handleClickGoing}
          className={classes.menus}
        >
          <MenuList>
            <div className="border-b-1 border-primary-200 px-5 py-2 text-primary text-12 lowercase">
              {formatMessage(
                {
                  id: 'notificationTimeline.card.event.action.going',
                },
                { count: going },
              )}
            </div>
          </MenuList>
          {renderSubscriberList(isLoading, subscriberList)}
        </Menus>
      </div>
    )
  }

  const handleMaybe = () => {
    return (
      <div>
        <CustomsButton
          size="small"
          aria-haspopup="true"
          aria-controls="maybe-list"
          disabled={maybe <= 0}
          onClick={handleClickMaybe}
        >
          {formatMessage(
            {
              id: 'notificationTimeline.card.event.action.maybe',
            },
            { count: maybe },
          )}
          <span>&#44;</span>
        </CustomsButton>
        <Menus
          id="maybe-list"
          anchorEl={anchorEl}
          open={isMaybe}
          onClose={handleClickMaybe}
          className={classes.menus}
        >
          <MenuList>
            <div className="border-b-1 border-primary-200 px-5 py-2 text-primary text-12 lowercase">
              {formatMessage(
                {
                  id: 'notificationTimeline.card.event.action.maybe',
                },
                { count: maybe },
              )}
            </div>
          </MenuList>
          {renderSubscriberList(isLoading, subscriberList)}
        </Menus>
      </div>
    )
  }

  const handleNotGoing = () => {
    return (
      <div>
        <CustomsButton
          size="small"
          aria-haspopup="true"
          aria-controls="not-going-list"
          disabled={notGoing <= 0}
          onClick={handleClickNotGoing}
        >
          {formatMessage(
            {
              id: 'notificationTimeline.card.event.action.notGoing',
            },
            { count: notGoing },
          )}
        </CustomsButton>
        <Menus
          id="not-going-list"
          anchorEl={anchorEl}
          open={isNotGoing}
          onClose={handleClickNotGoing}
          className={classes.menus}
        >
          <MenuList>
            <div className="border-b-1 border-primary-200 px-5 py-2 text-primary text-12 lowercase">
              {formatMessage(
                {
                  id: 'notificationTimeline.card.event.action.notGoing',
                },
                { count: notGoing },
              )}
            </div>
          </MenuList>
          {renderSubscriberList(isLoading, subscriberList)}
        </Menus>
      </div>
    )
  }

  return (
    <>
      {isNil(data) ? null : (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <PeopleAltOutlinedIcon
              className="mr-3 text-grey-200"
              fontSize="small"
            />
            {handleGoing()}
            {handleMaybe()}
            {handleNotGoing()}
          </div>
          <div className="space-x-5">
            <Button
              variant="contained"
              size="small"
              className="bg-transparent text-sky-700 font-normal text-14 p-0 shadow"
              onClick={() => handleDownload('csv', id)}
            >
              <DownloadIcon color="#075985" classes="w-16 h-16" />
              CSV
            </Button>
            <Button
              variant="contained"
              size="small"
              className="bg-transparent text-sky-700 font-normal text-14 p-0 shadow"
              onClick={() => handleDownload('pdf', id)}
            >
              <DownloadIcon color="#075985" classes="w-16 h-16" />
              PDF
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

EventInvitees.propTypes = {
  id: PropTypes.number,
  data: PropTypes.shape({
    going: PropTypes.number,
    maybe: PropTypes.number,
    notGoing: PropTypes.number,
  }),
}

export default EventInvitees
